import { useWatch, type FieldValues, type Resolver, type UseFormReturn } from "react-hook-form";
import type { ZodType } from "zod";

/**
 * A react-hook-form resolver for zod.
 *
 * Hand-rolled rather than pulling `@hookform/resolvers`, which declares an optional peer on
 * `ajv-formats@^2` and so cannot be installed beside the `ajv-formats@3` this repo pins for the
 * contract test (`test/contract.test.ts`, required by the contract obligation in ADR-005).
 * Forcing the install with --legacy-peer-deps would push that flag into CI for one dependency
 * whose zod adapter is this: parse, and map issues onto field paths.
 */
export function zodResolver<T extends FieldValues>(schema: ZodType<T>): Resolver<T> {
  return async (values) => {
    const result = schema.safeParse(values);
    if (result.success) return { values: result.data, errors: {} };

    const errors: Record<string, { type: string; message: string }> = {};
    for (const issue of result.error.issues) {
      // Dotted path so nested fields resolve; first issue per field wins, because showing a
      // field's second complaint before its first has been fixed is just noise.
      const path = issue.path.join(".");
      if (path && !errors[path]) errors[path] = { type: issue.code, message: issue.message };
    }
    return { values: {}, errors: errors as never };
  };
}

/** A box never ticked and a field never filled read the same, whatever the form stores for them. */
const isEmpty = (value: unknown) =>
  value === undefined || value === null || value === "" || value === false;

/** The same ids, in any order: a list of picks against what is saved. */
export function sameIds(a: readonly number[], b: readonly number[]): boolean {
  const inA = new Set(a);
  return inA.size === new Set(b).size && b.every((id) => inA.has(id));
}

/**
 * Whether a form's values are the same as the ones it started from, as a person reads them rather
 * than as `===` does: empty is empty whether it is `""`, `null`, `undefined`, an unticked `false` or
 * an empty list, and a number is the same as the text of that number, since an input hands back
 * text. Field order does not matter; list order does.
 */
export function sameValues(a: unknown, b: unknown): boolean {
  if (isEmpty(a) && isEmpty(b)) return true;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
      // An empty list is as empty as no list at all.
      return (Array.isArray(a) ? a.length === 0 : isEmpty(a)) && (Array.isArray(b) ? b.length === 0 : isEmpty(b));
    }
    return a.every((item, i) => sameValues(item, b[i]));
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    return [...keys].every((key) => sameValues(a[key], b[key]));
  }
  if (
    (typeof a === "number" && typeof b === "string") ||
    (typeof a === "string" && typeof b === "number")
  ) {
    return String(a) === String(b);
  }
  return Object.is(a, b);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" && value !== null && Object.getPrototypeOf(value) === Object.prototype
  );
}

/**
 * Whether a react-hook-form form holds values other than the ones it was given, watched as the
 * person edits. Unlike `formState.isDirty` it does not depend on each `setValue` passing
 * `shouldDirty`, and after `form.reset(saved)` the saved values are what it compares against.
 * `ignore` names fields that steer the form rather than hold anything to lose, such as a mode.
 */
export function useFormChanged<T extends FieldValues>(
  form: UseFormReturn<T>,
  ignore: readonly string[] = [],
): boolean {
  const values: Record<string, unknown> = { ...useWatch({ control: form.control }) };
  const defaults: Record<string, unknown> = { ...form.formState.defaultValues };
  for (const key of ignore) {
    delete values[key];
    delete defaults[key];
  }
  return !sameValues(values, defaults);
}
